import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import { handleAPIError, queryClient } from "utils";
import { toast } from "react-toastify";
import {
  createRole,
  deleteRole,
  getRole,
  getRoleById,
  updateRole,
} from "services";
import { RoleType } from "types";

// Fetch all Role
export const useGetRoles = () => {
  return useQuery({
    queryKey: [queryKeys.roles],
    queryFn: async () => await getRole(),
  });
};

// Fetch a single Role by ID
export const useGetRoleById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.roles, id],
    queryFn: async () => await getRoleById(id),
    enabled: !!id, // Only run query if
  });
};

// Create a new Role
export const useCreateRole = () => {
  return useMutation({
    mutationFn: createRole,
    onSuccess: async () => {
      toast.success("Role created successfully.");

      // Invalidate Role query on success to refresh the list
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.roles],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Define the UpdateRolePayload type
export type UpdateRolePayload = {
  id: string; // Required Role ID
  data: RoleType;
};

// Hook to update a Role
export const useUpdateRole = (): UseMutationResult<
  any,
  Error,
  UpdateRolePayload
> => {
  return useMutation<any, Error, UpdateRolePayload>({
    mutationFn: async ({ id, data }) => {
      return updateRole(id, data);
    },
    onSuccess: async () => {
      // Invalidate Role query on success
      toast.success("Role updated successfully.");

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.roles],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a Role
export const useDeleteRole = () => {
  return useMutation({
    mutationFn: deleteRole,
    onSuccess: async () => {
      toast.success("Role deleted successfully.");

      // Invalidate Role query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.roles],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
